/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author Shihan Rahim
 * 17/10/2022
 *
 */

import React from "react";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import { buildCoreLogsDefaultFilterUrl } from "../../../../../utils/coreLogsSearch";

class CoreLogFilterForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            logPartnerId: '',
            logPartner: '',
            logInstitutionId: '',
            logInstitution: '',
            logCourseName: '',
            logUserName: '',
            logLogLevel: '',
            logMsg: '',
            logCategory: '',            
            logHostName: '',

            logCreatedDateFrom: '',
            logCreatedDateTo: '',

            errors: {},

            // form fields
            fields: [
                {
                    inputType: 'dropdown',
                    labelName: 'Partner',
                    inputName: 'partnerId',
                    optionValueName: 'logPartnerId',
                    optionLabelName: 'logPartner',
                    required: true,
                    path: '/view-audit',
                    valueType: 'logTracePartners'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Institution',
                    inputName: 'logInstitution',
                    optionValueName: 'logInstitutionId',
                    optionLabelName: 'logInstitution',
                    required: false,
                    path: '/logs',
                    valueType: 'logTraceInstitutions',
                    partnerFilterStateKey: 'logPartner'
                },
                {
                    inputType: 'text',
                    labelName: 'Course',
                    inputName: 'logCourseName',
                    optionValueName: 'logCourseName',
                    optionLabelName: 'logCourseName',
                    placeholder: 'Please enter Course here',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'User',
                    inputName: 'logUserName',
                    optionValueName: 'logUserName',
                    optionLabelName: 'logUserName',
                    placeholder: 'Please enter User Name here',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Message',
                    inputName: 'logMsg',
                    optionValueName: 'logMsg',
                    optionLabelName: 'logMsg',
                    placeholder: 'Please enter Message here',
                    required: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Level',
                    inputName: 'logLogLevel',
                    optionValueName: 'logLogLevel',
                    optionLabelName: 'logLogLevel',
                    required: false,
                    path: '',
                    valueType: 'log-level'
                },
                {
                    inputType: 'text',
                    labelName: 'Category',
                    inputName: 'logCategory',
                    optionValueName: 'logCategory',
                    optionLabelName: 'logCategory',
                    placeholder: 'Please enter Category here',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Host Name',
                    inputName: 'logHostName',
                    optionValueName: 'logHostName',
                    optionLabelName: 'logHostName',
                    placeholder: 'Please enter Host Name here',
                    required: false
                },
                {
                    inputType: 'dateTime',
                    labelName: 'From',
                    inputName: 'logCreatedDateFrom',
                    optionValueName: 'logCreatedDateFrom',
                    optionLabelName: 'logCreatedDateFrom',
                    valueType: 'startDate',
                    syncInitialValue: false
                },
                {
                    inputType: 'dateTime',
                    labelName: 'To',
                    inputName: 'logCreatedDateTo',
                    optionValueName: 'logCreatedDateTo',
                    optionLabelName: 'logCreatedDateTo',
                    valueType: 'endDate',
                    syncInitialValue: false
                }
            ]
        };
        EventEmitter.emit('setPageHeaderData', {
            page: 'coreLogs',
            pageName: "View Log",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/coreLogs",
            optionalIcons: ['manageColumnsIcon']
        });
    }

    componentDidMount() {
    }

    /**
     * update the state when user inout a value
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionLabelName]: event.label,
                [event.optionValueName]: event.value
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            });
        }
    };

    /** Dropdown label format: "{institutionName} - ({partnerName})" */
    extractInstitutionName = (institutionLabel) => {
        if (!institutionLabel) {
            return '';
        }
        const marker = ' - (';
        const idx = institutionLabel.indexOf(marker);
        if (idx > 0) {
            return institutionLabel.substring(0, idx).trim();
        }
        const labelMatch = institutionLabel.trim().match(/^(.+?)\s+\([^)]+\)\s*$/);
        if (labelMatch) {
            return labelMatch[1].trim();
        }
        return institutionLabel.trim();
    };

    extractPartnerFromInstitutionLabel = (institutionLabel) => {
        const marker = ' - (';
        const idx = institutionLabel.indexOf(marker);
        if (idx > 0 && institutionLabel.endsWith(')')) {
            return institutionLabel.substring(idx + marker.length, institutionLabel.length - 1).trim();
        }
        const partnerMatch = institutionLabel.trim().match(/^.+?\s+\(([^)]+)\)\s*$/);
        if (partnerMatch) {
            return partnerMatch[1].trim();
        }
        return '';
    };

    formatDateForSearch = (value) => {
        if (!(value instanceof Date)) {
            return value;
        }
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        const hours = String(value.getHours()).padStart(2, '0');
        const minutes = String(value.getMinutes()).padStart(2, '0');
        return `${value.getFullYear()}-${month}-${day}%20${hours}:${minutes}`;
    };

    appendSearchParam = (search, key, value) => search.concat(key, ':', value, '%2C');

    loadFilteredResults = () => {
        let search = '&search=%7B'

        // get required fields
        for (const [key, value] of Object.entries(this.state)) {
            switch (key) {
                case 'logInstitution':
                    if (value !== "") {
                        const institutionName = this.extractInstitutionName(value);
                        if (this.state.logPartner === "") {
                            const partner = this.extractPartnerFromInstitutionLabel(value);
                            if (partner !== "") {
                                search = this.appendSearchParam(search, 'logPartner', partner);
                            }
                        }
                        search = this.appendSearchParam(search, key, institutionName);
                    }
                    break;
                case 'logPartner':
                case 'logCourseName':
                case 'logUserName':
                case 'logMsg':
                case 'logLogLevel':
                case 'logCategory':
                case 'logHostName':
                case 'logCreatedDateFrom':
                case 'logCreatedDateTo':
                    if (value !== '') {
                        const paramValue = this.formatDateForSearch(value);
                        if (typeof paramValue === 'string' && paramValue !== '') {
                            search = this.appendSearchParam(search, key, paramValue);
                        }
                    }
                    break;
                default:
            }
        }

        // remove the last %2C and add to the url
        if (search.length === 11) {
            search = '';
        } else if (search.length > 10) {
            search = search.slice(0, -3).concat('%7D')
        } else {
            search = '';
        }

        // publish the url to the table component
        EventEmitter.emit('tableFilterUrl', search)
        EventEmitter.emit('openNav', '')
    };

    clearFilteredResults = () => {
        const newState = Object.assign({}, this.state);
        // newState.logCreatedDateFrom = '';
        // newState.logCreatedDateTo = '';

        newState.logPartnerId = '';
        newState.logPartner = '';
        newState.logInstitutionId = '';
        newState.logInstitution = '';
        newState.logCourseName = '';
        newState.logUserName = '';
        newState.logMsg = '';
        newState.logLogLevel = '';
        newState.logCategory = '';
        newState.logHostName = '';

        newState.errors = {};

        this.setState(newState);

        EventEmitter.emit('tableFilterUrl', buildCoreLogsDefaultFilterUrl());
        EventEmitter.emit('openNav', '');
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    render() {
        return(
            <div className="view-error-filter-container">
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
                <div className="view-audit-filter-button-container">
                    <input type="button" className="view-audit-filter-cancel" value="Clear"
                           onClick={() => this.clearFilteredResults()} />
                    <input type="button" className="view-audit-filter-submit" value="Filter"
                           onClick={() => this.loadFilteredResults()}/>
                </div>
            </div>
        )
    }
}

export default CoreLogFilterForm;